import {Box, Hide, Show, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Fragment} from 'react';

import {isNotNil} from 'ramda';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {emailHtmlToPlainText} from '../../../utils/emailHtmlToPlainText';
import {getChangedExcerpt} from '../getChangedExcerpt';
import {getWordDiff, isDiffInputTooLarge, WordDiffPartType} from '../getWordDiff';

const ELLIPSIS = '\u2026';

interface AiComposerChangedTextProps extends RequiredTestIdProps {
  previousBody: string;
  currentBody: string;
  isHtml: boolean;
}

interface RenderablePart {
  value: string;
  type: WordDiffPartType;
  isTruncated?: boolean;
}

const getPartColor = (type: WordDiffPartType) =>
  match(type)
    .with('added', () => 'success' as const)
    .with('removed', () => 'danger' as const)
    .otherwise(() => 'primary' as const);

const TRAILING_SPACE = /\s$/;

/**
 * A removed run followed directly by its replacement read as one word ("napištepřípadě", seen in
 * review), so a space separates the two.
 */
const needsSeparator = (part: RenderablePart, next: RenderablePart | undefined) =>
  part.type === 'removed' && next?.type === 'added' && !TRAILING_SPACE.test(part.value);

const renderPart = (part: RenderablePart, index: number, parts: RenderablePart[]) => (
  <Fragment key={`${index}-${part.type}`}>
    <Text
      inline
      preWrap
      overflowWrap="anywhere"
      size="small"
      strikethrough={part.type === 'removed'}
      color={getPartColor(part.type)}
    >
      {part.value}
    </Text>
    <Show when={needsSeparator(part, parts.at(index + 1))}>
      <Text inline size="small">
        {' '}
      </Text>
    </Show>
    <Show when={part.isTruncated}>
      <Text inline size="small" color="tertiary">
        {ELLIPSIS}
      </Text>
    </Show>
  </Fragment>
);

export function AiComposerChangedText(props: AiComposerChangedTextProps) {
  const previous = props.isHtml ? emailHtmlToPlainText(props.previousBody) : props.previousBody;
  const current = props.isHtml ? emailHtmlToPlainText(props.currentBody) : props.currentBody;
  const diff = isDiffInputTooLarge(previous, current) ? null : getWordDiff(previous, current);
  const excerpt = isNotNil(diff) ? null : getChangedExcerpt(previous, current);

  return (
    <Box data-testid={suffixTestId('changedText', props)}>
      <Show when={isNotNil(diff)}>{(diff ?? []).map(renderPart)}</Show>
      <Hide when={isNotNil(diff)}>
        <Show when={excerpt?.hasPrecedingText}>
          <Text inline size="small" color="tertiary">
            {ELLIPSIS}
          </Text>
        </Show>
        {(excerpt?.parts ?? []).map(renderPart)}
      </Hide>
    </Box>
  );
}
